// src/pages/ResetPasswordPage.jsx
// ============================================================
// Paso 3 de 3: contraseña nueva, con el ticket del paso anterior.
//
// La validación de la contraseña está duplicada del backend a propósito. No
// reemplaza a aquella —el backend valida igual— sino que evita que un typo
// queme uno de los 8 intentos cada 15 minutos que permite /confirm.
//
// El aviso de que esto cierra todas las sesiones va ANTES del botón: el
// backend revoca todos los refresh tokens del usuario, así que si estaba
// logueado en otro dispositivo, queda afuera. Es lo correcto —recuperar una
// cuenta tomada tiene que echar al que estaba— pero enterarse después es una
// sorpresa.
//
// ⚠ Sobre el interceptor de 401 de src/api/api.js: ver la nota extensa en
//    ForgotPasswordPage.jsx. Resumen: hoy no aplica porque estos endpoints
//    devuelven 400 y nunca 401; si eso cambiara, el usuario sería expulsado
//    al inicio en lugar de ver el error.
// ============================================================

import { useState } from 'react';
import { Box, TextField, Button, Typography, CircularProgress, IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate, useLocation, Navigate, Link as RouterLink } from 'react-router-dom';
import api from '../api/api';
import AuthCard from '../components/AuthCard';

// Mismo criterio que routes/users.js y /auth/password-reset/confirm.
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const email  = location.state?.email;
  const ticket = location.state?.ticket;

  const [password, setPassword] = useState('');
  const [repetir, setRepetir]   = useState('');
  const [ver, setVer]           = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [listo, setListo]       = useState(false);

  // Entrada directa a /recuperar/clave sin pasar por los dos pasos previos.
  if (!email || !ticket) return <Navigate to="/recuperar" replace />;

  const confirmar = async () => {
    setError('');

    if (!PASSWORD_REGEX.test(password)) {
      setError('Al menos 8 caracteres, con mayúscula, minúscula, número y símbolo.');
      return;
    }
    if (password !== repetir) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/password-reset/confirm', { email, ticket, newPassword: password });
      setListo(true);
      // Un respiro para que se lea el mensaje antes de mandarlo al login.
      setTimeout(() => navigate('/', { replace: true }), 2200);
    } catch (e) {
      setError(e.response?.data?.error || 'No pudimos cambiar la contraseña. Empezá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (listo) {
    return (
      <AuthCard
        titulo="Listo"
        subtitulo="Tu contraseña quedó cambiada. Te llevamos al inicio para que entres."
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={26} sx={{ color: '#E7A432' }} />
        </Box>
      </AuthCard>
    );
  }

  return (
    <AuthCard titulo="Contraseña nueva" subtitulo={`Elegí una contraseña para ${email}`}>
      <TextField
        fullWidth
        placeholder="Contraseña nueva"
        type={ver ? 'text' : 'password'}
        value={password}
        onChange={e => setPassword(e.target.value)}
        disabled={loading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setVer(v => !v)}
                edge="end"
                aria-label={ver ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {ver ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 } }}
      />

      <TextField
        fullWidth
        placeholder="Repetir contraseña"
        type={ver ? 'text' : 'password'}
        value={repetir}
        onChange={e => setRepetir(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') confirmar(); }}
        disabled={loading}
        sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 } }}
      />

      <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', textAlign: 'center', mb: 1.5 }}>
        Al menos 8 caracteres, con mayúscula, minúscula, número y símbolo.
      </Typography>

      {error && (
        <Typography sx={{ fontSize: 13, color: '#f87171', textAlign: 'center', mb: 1.5 }}>
          {error}
        </Typography>
      )}

      <Typography sx={{ fontSize: 12, color: '#E7A432', textAlign: 'center', mb: 2 }}>
        Por seguridad, esto cierra todas las sesiones abiertas de tu cuenta.
      </Typography>

      <Button
        fullWidth
        onClick={confirmar}
        disabled={loading}
        sx={{
          bgcolor: '#E7A432', color: '#163244', borderRadius: 2, py: 1.4,
          fontWeight: 700, fontSize: 15,
          '&:hover': { bgcolor: '#f5bc55' },
        }}
      >
        {loading ? <CircularProgress size={22} sx={{ color: '#163244' }} /> : 'Cambiar contraseña'}
      </Button>

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography
          component={RouterLink}
          to="/"
          sx={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', textDecoration: 'none' }}
        >
          Volver a <Box component="span" sx={{ color: '#E7A432', fontWeight: 600 }}>iniciar sesión</Box>
        </Typography>
      </Box>
    </AuthCard>
  );
}
